import { useState, useEffect, useMemo } from "react";
import { useGet, useCreate, useDelete, useUpdate } from "hooks";
import { useAuth } from "pages/AuthProvider";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  Checkbox,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

// Alt Text Cell Component
const AltTextCell = ({ id, value, onUpdate }) => {
  const [text, setText] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const { update } = useUpdate("/api/media");

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await update(id, { altText: text });
      onUpdate?.(id, text);
    } catch (err) {
      console.error("Failed to update alt text:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      <TextField
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSave();
          }
        }}
        sx={{ minWidth: 150 }}
        placeholder="Enter alt text..."
        disabled={isSaving}
      />
      {text !== (value || "") && (
        <Button
          size="small"
          variant="contained"
          sx={{ minWidth: 60 }}
          onClick={handleSave}
          disabled={isSaving}
        >
          Save
        </Button>
      )}
    </Box>
  );
};

// Pagination Component
const PaginationControl = ({ paginationModel, setPaginationModel, total, currentCount }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      p: 2,
      borderTop: "1px solid #e0e0e0",
      flexShrink: 0,
      backgroundColor: "#fafafa",
      flexWrap: "wrap",
      gap: 2,
    }}
  >
    <Typography variant="body2" color="textSecondary">
      Showing{" "}
      {currentCount === 0
        ? 0
        : paginationModel.page * paginationModel.pageSize + 1}{" "}
      -{" "}
      {Math.min(
        (paginationModel.page + 1) * paginationModel.pageSize,
        total
      )}{" "}
      of {total} items
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Typography variant="body2">Items per page:</Typography>
      <select
        value={paginationModel.pageSize}
        onChange={(e) =>
          setPaginationModel({
            page: 0,
            pageSize: Number(e.target.value),
          })
        }
        style={{
          padding: "8px 12px",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          fontSize: "14px",
          cursor: "pointer",
          backgroundColor: "#fff",
        }}
      >
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={250}>250</option>
        <option value={500}>500</option>
        <option value={1000}>1000</option>
      </select>
    </Box>
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        variant="outlined"
        size="small"
        disabled={paginationModel.page === 0}
        onClick={() =>
          setPaginationModel((p) => ({
            ...p,
            page: Math.max(0, p.page - 1),
          }))
        }
      >
        Previous
      </Button>
      <Typography
        variant="body2"
        sx={{ display: "flex", alignItems: "center", px: 2 }}
      >
        Page {paginationModel.page + 1} of{" "}
        {Math.ceil(total / paginationModel.pageSize) || 1}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        disabled={
          paginationModel.page >=
          Math.ceil(total / paginationModel.pageSize) - 1
        }
        onClick={() =>
          setPaginationModel((p) => ({
            ...p,
            page: p.page + 1,
          }))
        }
      >
        Next
      </Button>
    </Box>
  </Box>
);

const MediaLibrary = () => {
  const { canAccess } = useAuth();
  const [selected, setSelected] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sidebarAltText, setSidebarAltText] = useState({});

  const endpoint = `/api/media/all?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}&search=${encodeURIComponent(
    search
  )}`;
  const { data, loading, mutate } = useGet(endpoint);
  const { create } = useCreate("/api/media/upload");
  const { remove } = useDelete("/api/media");
  const { update: updateMedia } = useUpdate("/api/media");

  // 🔼 UPLOAD
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    const formData = new FormData();
    files.forEach((file) => formData.append("media", file));

    try {
      setUploading(true);
      await create(formData, { revalidate: false });
      await mutate();
    } finally {
      setUploading(false);
    }
  };

  // ✅ SELECT
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // ❌ BULK DELETE
  const { create: createForBulk } = useCreate("/api/media/bulk-delete");
  const bulkDelete = async () => {
    await createForBulk({ ids: selected }, { revalidate: false });
    setSelected([]);
    await mutate();
  };

  // Get items from API response
  const items = useMemo(() => {
    return data?.data || data?.items || data?.media || data || [];
  }, [data]);

  const total = useMemo(() => {
    return data?.total ?? data?.count ?? 0;
  }, [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "sr",
        header: "Sr No",
        cell: (info) =>
          paginationModel.page * paginationModel.pageSize +
          info.row.index +
          1,
        size: 70,
      },
      {
        accessorKey: "preview",
        header: "Preview",
        cell: (info) => {
          const item = info.row.original;
          const isVideo =
            item?.type?.startsWith("video") ||
            /\.(mp4|webm|mov|mkv)$/i.test(item?.url || item?.name || "");

          return (
            <Tooltip
              arrow
              placement="right"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(255, 255, 255, 0.98)",
                    color: "text.primary",
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                    border: "1px solid #e0e0e0",
                    p: 1.5,
                    maxWidth: 320,
                    borderRadius: 2,
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                    "&::before": { border: "1px solid #e0e0e0" },
                  },
                },
              }}
              title={
                <Box sx={{ textAlign: "center" }}>
                  {isVideo ? (
                    <video
                      src={item.url}
                      muted
                      autoPlay
                      loop
                      style={{
                        maxWidth: 280,
                        maxHeight: 200,
                        borderRadius: 6,
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={item.url}
                      alt={item.altText || item.name || "Preview"}
                      sx={{
                        maxWidth: 280,
                        maxHeight: 280,
                        objectFit: "contain",
                        borderRadius: 1,
                        display: "block",
                        mx: "auto",
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1,
                      fontWeight: 600,
                      color: "#333",
                      maxWidth: 280,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name || "Preview"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#777", fontSize: 11, display: "block" }}
                  >
                    Click to view full preview
                  </Typography>
                </Box>
              }
            >
              <Box
                onClick={() => setPreviewItem(item)}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#1976d2",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.08)",
                  },
                }}
              >
                {isVideo ? (
                  <video
                    src={item.url}
                    muted
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={item.url}
                    alt={item.altText || item.name || "Preview"}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          );
        },
        size: 80,
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: (info) => info.getValue() || "N/A",
      },
      {
        accessorKey: "url",
        header: "File",
        cell: (info) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <a
              href={info.getValue()}
              target="_blank"
              rel="noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {info.getValue()?.substring(0, 50)}...
            </a>
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(info.getValue())}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (info) => {
          const type = info.getValue();
          if (typeof type === "string") {
            if (type.startsWith("image")) return "Image";
            if (type.startsWith("video")) return "Video";
          }
          return type || "N/A";
        },
        size: 100,
      },
      {
        accessorKey: "altText",
        header: "Alt Text",
        cell: (info) => (
          <AltTextCell
            id={info.row.original._id}
            value={info.getValue()}
            onUpdate={(id, text) => {
              mutate();
            }}
          />
        ),
      },
      {
        accessorKey: "_id",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              color="primary"
              size="small"
              variant="text"
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => setPreviewItem(info.row.original)}
            >
              View
            </Button>
            {canAccess('media', 'delete') && (
              <Button
                color="error"
                size="small"
                variant="text"
                onClick={async () => {
                  try {
                    await remove(info.getValue(), { revalidate: false });
                    await mutate();
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        ),
        size: 160,
      },
    ],
    [paginationModel, canAccess]
  );

  // Create table instance
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: {
      pagination: paginationModel,
    },
  });

  useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [search]);

  useEffect(() => {
    console.log("Endpoint:", endpoint);
  }, [endpoint]);

  return (
    <Box p={2} bgcolor="#f0f0f1" minHeight="100vh" display="flex" flexDirection="column">
      <Typography variant="h5" mb={2}>
        Media Library
      </Typography>

      {/* 🔼 UPLOADER */}
      {canAccess('media', 'create') && (
        <Box
          p={3}
          mb={2}
          bgcolor="#fff"
          border="2px dashed #2271b1"
          textAlign="center"
        >
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            Upload Files
            <input hidden multiple type="file" onChange={handleUpload} />
          </Button>

          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      )}

      {/* 🔎 TOOLBAR */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search media by name, URL, type, or alt text..."
          onChange={(e) => setSearch(e.target.value)}
        />

        <ToggleButtonGroup
          size="small"
          value={view}
          exclusive
          onChange={(e, v) => v && setView(v)}
        >
          <ToggleButton value="grid">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="table">Table</ToggleButton>
          <ToggleButton value="list">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        {selected.length > 0 && canAccess('media', 'delete') && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={bulkDelete}
          >
            Delete ({selected.length})
          </Button>
        )}
      </Box>

      {/* 🖼 GRID VIEW */}
      {view === "grid" && (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {loading ? (
            <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={1}>
              <LinearProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={1}>
              <Typography>No media found.</Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {items.map((item) => (
                  <Grid item xs={6} sm={4} md={2} key={item._id}>
                    <Card
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        borderRadius: 1.5,
                        overflow: "hidden",
                        border: "1px solid #e0e0e0",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          transform: "translateY(-2px)",
                        },
                      }}
                      onClick={() => setActiveItem(item)}
                    >
                      <Checkbox
                        checked={selected.includes(item._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelect(item._id);
                        }}
                        sx={{
                          position: "absolute",
                          zIndex: 2,
                          top: 4,
                          left: 4,
                          bgcolor: "rgba(255,255,255,0.7)",
                          borderRadius: 1,
                          p: 0.5,
                        }}
                      />
                      <CardMedia
                        component="img"
                        height="130"
                        image={item.url}
                        alt={item.name}
                        sx={{ objectFit: "cover" }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Paper sx={{ bgcolor: "#fff", mt: "auto", borderRadius: 1, overflow: "hidden" }}>
                <PaginationControl
                  paginationModel={paginationModel}
                  setPaginationModel={setPaginationModel}
                  total={total}
                  currentCount={items.length}
                />
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* 📋 TABLE VIEW */}
      {view === "table" && (
        <Paper sx={{ 
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden"
        }}>
          {loading ? (
            <Box p={4} textAlign="center">
              <LinearProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography>No media found.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ 
                flex: 1,
                minHeight: 0,
                overflow: "auto"
              }}>
                <Table stickyHeader>
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ width: 50 }}>
                        <Checkbox
                          indeterminate={
                            selected.length > 0 && selected.length < items.length
                          }
                          checked={
                            items.length > 0 &&
                            selected.length === items.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelected(items.map((item) => item._id));
                            } else {
                              setSelected([]);
                            }
                          }}
                        />
                      </TableCell>
                      {table.getHeaderGroups().map((headerGroup) =>
                        headerGroup.headers.map((header) => (
                          <TableCell
                            key={header.id}
                            sx={{ fontWeight: 600 }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableCell>
                        ))
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          bgcolor: selected.includes(row.original._id)
                            ? "#e3f2fd"
                            : "inherit",
                        }}
                      >
                        <TableCell sx={{ width: 50 }}>
                          <Checkbox
                            checked={selected.includes(row.original._id)}
                            onChange={() => toggleSelect(row.original._id)}
                          />
                        </TableCell>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <PaginationControl
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                total={total}
                currentCount={items.length}
              />
            </>
          )}
        </Paper>
      )}

      {/* 📃 LIST VIEW */}
      {view === "list" && (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {loading ? (
            <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={1}>
              <LinearProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={1}>
              <Typography>No media found.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                {items.map((item) => (
                  <Box
                    key={item._id}
                    display="flex"
                    alignItems="center"
                    bgcolor="#fff"
                    p={1.5}
                    mb={1}
                    borderRadius={1}
                    border="1px solid #e0e0e0"
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { bgcolor: "#f8f9fa" },
                    }}
                    onClick={() => setActiveItem(item)}
                  >
                    <Checkbox
                      checked={selected.includes(item._id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(item._id);
                      }}
                    />
                    <Box
                      component="img"
                      src={item.url}
                      width={60}
                      height={60}
                      alt={item.name}
                      sx={{ objectFit: "cover", borderRadius: 1, ml: 1 }}
                    />
                    <Typography ml={2} fontWeight={500}>{item.name}</Typography>
                  </Box>
                ))}
              </Box>
              <Paper sx={{ bgcolor: "#fff", mt: "auto", borderRadius: 1, overflow: "hidden" }}>
                <PaginationControl
                  paginationModel={paginationModel}
                  setPaginationModel={setPaginationModel}
                  total={total}
                  currentCount={items.length}
                />
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* 👉 RIGHT SIDEBAR (DETAILS) */}
      <Drawer
        anchor="right"
        open={Boolean(activeItem)}
        onClose={() => setActiveItem(null)}
      >
        {activeItem && (
          <Box width={320} p={2}>
            <Typography variant="h6">Attachment Details</Typography>
            <img
              src={activeItem.url}
              style={{ width: "100%", marginTop: 10 }}
              alt={activeItem.name}
            />
            <Typography mt={2}>
              <b>File:</b> {activeItem.name}
            </Typography>
            <Typography>
              <b>Type:</b> {activeItem.type}
            </Typography>
            <Typography>
              <b>Uploaded:</b> {activeItem.createdAt}
            </Typography>
            <Typography mt={2}>
              <b>Alt Text:</b>
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              value={sidebarAltText[activeItem._id] !== undefined ? sidebarAltText[activeItem._id] : (activeItem.altText || "")}
              onChange={(e) =>
                setSidebarAltText((prev) => ({
                  ...prev,
                  [activeItem._id]: e.target.value,
                }))
              }
              placeholder="Enter alt text for accessibility..."
              disabled={!canAccess('media', 'edit')}
              sx={{ mt: 1 }}
            />
            {canAccess('media', 'edit') && (
              <Button
                fullWidth
                variant="contained"
                size="small"
                sx={{ mt: 2 }}
                onClick={async () => {
                  try {
                    const altText = sidebarAltText[activeItem._id] !== undefined ? sidebarAltText[activeItem._id] : (activeItem.altText || "");
                    await updateMedia(activeItem._id, { altText });
                    setSidebarAltText((prev) => {
                      const newState = { ...prev };
                      delete newState[activeItem._id];
                      return newState;
                    });
                    setActiveItem(null);
                    await mutate();
                  } catch (err) {
                    console.error("Failed to update alt text:", err);
                  }
                }}
              >
                Save Alt Text
              </Button>
            )}
          </Box>
        )}
      </Drawer>

      {/* 🔍 FULL PREVIEW MODAL */}
      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 2, overflow: "hidden" },
          },
        }}
      >
        {previewItem && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#f9f9f9",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: "80%" }}>
                {previewItem.name || "Media Preview"}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => setPreviewItem(null)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#1a1a1a",
                minHeight: 350,
              }}
            >
              {previewItem.type?.startsWith("video") || /\.(mp4|webm|mov|mkv)$/i.test(previewItem.url || "") ? (
                <video
                  src={previewItem.url}
                  controls
                  autoPlay
                  style={{
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewItem.url}
                  alt={previewItem.altText || previewItem.name}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    objectFit: "contain",
                    borderRadius: 1,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                  }}
                />
              )}
            </DialogContent>
            <DialogActions
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                bgcolor: "#f9f9f9",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  href={previewItem.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Original
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => navigator.clipboard.writeText(previewItem.url)}
                >
                  Copy URL
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    const item = previewItem;
                    setPreviewItem(null);
                    setActiveItem(item);
                  }}
                >
                  Edit Details
                </Button>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => setPreviewItem(null)}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MediaLibrary;
